import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
    makeStyles,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    IconButton,
    TextField,
    InputAdornment,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Grid,
    Card,
    CardContent,
    Chip,
    TablePagination,
} from "@material-ui/core";
import {
    ArrowBack as ArrowBackIcon,
    Search as SearchIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Email as EmailIcon,
    Mouse as MouseIcon,
} from "@material-ui/icons";
import { format, parseISO } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";

const useStyles = makeStyles((theme) => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(2),
    },
    statsCard: {
        height: "100%",
    },
    statsValue: {
        fontSize: "2rem",
        fontWeight: "bold",
        color: theme.palette.primary.main,
    },
    statsLabel: {
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(1),
    },
    filters: {
        padding: theme.spacing(2),
        display: "flex",
        gap: theme.spacing(2),
        alignItems: "center",
        flexWrap: "wrap",
    },
    statusIcon: {
        fontSize: "1.2rem",
    },
}));

const EmailCampaignDetails = () => {
    const classes = useStyles();
    const history = useHistory();
    const { campaignId } = useParams();

    const [loading, setLoading] = useState(false);
    const [campaign, setCampaign] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [searchParam, setSearchParam] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [engagementFilter, setEngagementFilter] = useState("all");
    const [pageNumber, setPageNumber] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [totalCount, setTotalCount] = useState(0);

    useEffect(() => {
        fetchCampaign();
    }, [campaignId]);

    useEffect(() => {
        fetchContacts();
    }, [searchParam, statusFilter, engagementFilter, pageNumber]);

    const fetchCampaign = async () => {
        try {
            const { data } = await api.get(`/email-campaigns/${campaignId}`);
            setCampaign(data);
        } catch (err) {
            toast.error("Erro ao carregar campanha");
            history.push("/email-campaigns");
        }
    };

    const fetchContacts = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/email-campaigns/${campaignId}/contacts`, {
                params: {
                    searchParam,
                    status: statusFilter,
                    engagement: engagementFilter,
                    page: pageNumber,
                },
            });
            setContacts(data.contacts);
            setHasMore(data.hasMore);
            setTotalCount(data.count);
        } catch (err) {
            toast.error("Erro ao carregar contatos");
        }
        setLoading(false);
    };

    const getStatusChip = (status) => {
        const statusMap = {
            pending: { label: "Pendente", color: "default" },
            sent: { label: "Enviado", color: "primary" },
            failed: { label: "Falha", color: "secondary" },
        };
        const { label, color } = statusMap[status] || statusMap.pending;
        return <Chip label={label} color={color} size="small" />;
    };

    if (!campaign) {
        return <div>Carregando...</div>;
    }

    const openRate = campaign.sentCount > 0
        ? Math.round((campaign.openedCount / campaign.sentCount) * 100)
        : 0;

    const clickRate = campaign.sentCount > 0
        ? Math.round((campaign.clickedCount / campaign.sentCount) * 100)
        : 0;

    return (
        <MainContainer>
            <MainHeader>
                <Grid container alignItems="center" spacing={2}>
                    <Grid item>
                        <IconButton onClick={() => history.push("/email-campaigns")}>
                            <ArrowBackIcon />
                        </IconButton>
                    </Grid>
                    <Grid item>
                        <Title>{campaign.name}</Title>
                    </Grid>
                </Grid>
            </MainHeader>

            {/* Analytics Cards */}
            <Grid container spacing={1} style={{ marginBottom: 16 }}>
                <Grid item xs={12} sm={6} lg={true}>
                    <Card className={classes.statsCard}>
                        <CardContent style={{ padding: 12 }}>
                            <Typography className={classes.statsValue} style={{ fontSize: "1.5rem" }}>
                                {campaign.totalContacts}
                            </Typography>
                            <Typography className={classes.statsLabel} style={{ fontSize: "0.75rem" }}>
                                Total de Contatos
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} lg={true}>
                    <Card className={classes.statsCard}>
                        <CardContent style={{ padding: 12 }}>
                            <Typography className={classes.statsValue} style={{ fontSize: "1.5rem" }}>
                                {campaign.sentCount}
                            </Typography>
                            <Typography className={classes.statsLabel} style={{ fontSize: "0.75rem" }}>
                                Enviados ({Math.round((campaign.sentCount / campaign.totalContacts) * 100)}%)
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} lg={true}>
                    <Card className={classes.statsCard}>
                        <CardContent style={{ padding: 12 }}>
                            <Typography className={classes.statsValue} style={{ fontSize: "1.5rem", color: "#4caf50" }}>
                                {campaign.openedCount}
                            </Typography>
                            <Typography className={classes.statsLabel} style={{ fontSize: "0.75rem" }}>
                                Abertos ({openRate}%)
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} lg={true}>
                    <Card className={classes.statsCard}>
                        <CardContent style={{ padding: 12 }}>
                            <Typography className={classes.statsValue} style={{ fontSize: "1.5rem", color: "#2196f3" }}>
                                {campaign.clickedCount}
                            </Typography>
                            <Typography className={classes.statsLabel} style={{ fontSize: "0.75rem" }}>
                                Cliques ({clickRate}%)
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} lg={true}>
                    <Card className={classes.statsCard}>
                        <CardContent style={{ padding: 12 }}>
                            <Typography className={classes.statsValue} style={{ fontSize: "1.5rem", color: "#f44336" }}>
                                {campaign.failedCount}
                            </Typography>
                            <Typography className={classes.statsLabel} style={{ fontSize: "0.75rem" }}>
                                Falhas
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Paper className={classes.mainPaper} variant="outlined">
                {/* Filters */}
                <div className={classes.filters}>
                    <TextField
                        placeholder="Buscar por email, nome ou telefone..."
                        type="search"
                        value={searchParam}
                        onChange={(e) => setSearchParam(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon style={{ color: "gray" }} />
                                </InputAdornment>
                            ),
                        }}
                        style={{ minWidth: 300 }}
                    />
                    <FormControl style={{ minWidth: 150 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <MenuItem value="all">Todos</MenuItem>
                            <MenuItem value="sent">Enviados</MenuItem>
                            <MenuItem value="pending">Pendentes</MenuItem>
                            <MenuItem value="failed">Falhas</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl style={{ minWidth: 150 }}>
                        <InputLabel>Engajamento</InputLabel>
                        <Select
                            value={engagementFilter}
                            onChange={(e) => setEngagementFilter(e.target.value)}
                        >
                            <MenuItem value="all">Todos</MenuItem>
                            <MenuItem value="opened">Abriram</MenuItem>
                            <MenuItem value="not-opened">Não Abriram</MenuItem>
                            <MenuItem value="clicked">Clicaram</MenuItem>
                        </Select>
                    </FormControl>
                    <Typography variant="body2" color="textSecondary">
                        {totalCount} contato(s) encontrado(s)
                    </Typography>
                </div>

                {/* Contact List */}
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Email</TableCell>
                                <TableCell>Nome</TableCell>
                                <TableCell>Telefone</TableCell>
                                <TableCell align="center">Status</TableCell>
                                <TableCell align="center">Enviado Em</TableCell>
                                <TableCell align="center">Aberto</TableCell>
                                <TableCell align="center">Clicado</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRowSkeleton columns={7} />
                            ) : (
                                <>
                                    {contacts.map((contact) => (
                                        <TableRow key={contact.id}>
                                            <TableCell>{contact.email}</TableCell>
                                            <TableCell>{contact.name || "-"}</TableCell>
                                            <TableCell>{contact.phone || "-"}</TableCell>
                                            <TableCell align="center">
                                                {getStatusChip(contact.status)}
                                            </TableCell>
                                            <TableCell align="center">
                                                {contact.sentAt
                                                    ? format(parseISO(contact.sentAt), "dd/MM/yyyy HH:mm", { locale: ptBR })
                                                    : "-"}
                                            </TableCell>
                                            <TableCell align="center">
                                                {contact.opened ? (
                                                    <div>
                                                        <EmailIcon className={classes.statusIcon} style={{ color: "#4caf50" }} />
                                                        <Typography variant="caption" display="block">
                                                            {format(parseISO(contact.openedAt), "dd/MM HH:mm", { locale: ptBR })}
                                                        </Typography>
                                                    </div>
                                                ) : (
                                                    <CancelIcon className={classes.statusIcon} style={{ color: "#ccc" }} />
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                {contact.clicked ? (
                                                    <div>
                                                        <MouseIcon className={classes.statusIcon} style={{ color: "#2196f3" }} />
                                                        <Typography variant="caption" display="block">
                                                            {format(parseISO(contact.clickedAt), "dd/MM HH:mm", { locale: ptBR })}
                                                        </Typography>
                                                    </div>
                                                ) : (
                                                    <CancelIcon className={classes.statusIcon} style={{ color: "#ccc" }} />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {contacts.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center">
                                                Nenhum contato encontrado
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={pageNumber - 1}
                    onPageChange={(e, newPage) => setPageNumber(newPage + 1)}
                    rowsPerPage={20}
                    rowsPerPageOptions={[20]}
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    labelRowsPerPage="Registros por página:"
                />
            </Paper>
        </MainContainer>
    );
};

export default EmailCampaignDetails;
